const { replace } = require('connected-react-router');
const Continuation = require('./continuation');
const RoutingTable = require('./routing-table');
const Diagnostics = require('utils/app-diagnostics');

module.exports = { openAppDestination, navigateToPath, getAppAuthenticationRoute };

async function openAppDestination({ path, store, context = require('app-context').context, isCurrent = () => true, record = Diagnostics.record }) {

    const { actions, handlers, selectors: { all: selectors } } = context;
    Continuation.save({ redirect: RoutingTable.REDIRECTS.afterAuth, target: path });

    record('auth.wait');
    await handlers.auth.resolveAuthStatus();
    record('auth.ready', Diagnostics.getAppState({ selectors, state: store.getState(), location: window.location }));
    if (!isCurrent()) {
        record('handler.superseded');
        return;
    }

    const requireAuthentication = () => {

        const state = store.getState();
        if (selectors.getIsAuthenticated(state) && selectors.getVerifiedStatus(state)) {
            return false;
        }

        if (!selectors.getIsAuthenticated(state) && actions?.alerts?.notification?.push) {
            store.dispatch(actions.alerts.notification.push({ displayType: 'snackbar',
                message: 'Log in to open this link. We’ll return you to it afterward.' }));
        }

        navigateToPath({ path: getAppAuthenticationRoute({ selectors, state }), store, record });
        return true;
    };

    if (requireAuthentication()) {
        return;
    }

    record('resume.wait');
    // Loaded lazily: Cordova push handling also uses this shared destination flow.
    await require('utils/cordova').onResume();
    record('resume.ready');

    if (!isCurrent()) {
        record('handler.superseded');
        return;
    }

    const state = store.getState();

    if (requireAuthentication()) {
        return;
    }

    const requestedSchoolId = RoutingTable.getRequestedSchoolId(new URL(path, 'https://nearpeer.net'));
    const currentSchoolId = selectors.getCurrentSchoolId(state);
    const needsBootstrap = selectors.getMainAppLoading(state)
        || !selectors.hasCurrentUser(state)
        || !selectors.hasCurrentSchool(state)
        || (requestedSchoolId && requestedSchoolId !== Number(currentSchoolId));

    if (needsBootstrap) {
        record('bootstrap.wait', { requestedSchoolId, currentSchoolId });
        const result = await store.dispatch(actions.auth.bootstrapMainApp({
            schoolId: requestedSchoolId || currentSchoolId,
            redirect: RoutingTable.REDIRECTS.afterAuth,
            target: path
        }));
        record('bootstrap.settled', Diagnostics.getAppState({ selectors, state: store.getState(), location: window.location }));
        return result;
    }

    Continuation.consume();
    navigateToPath({ path, store, record });
}

function getAppAuthenticationRoute({ selectors, state }) {

    if (!selectors.getIsAuthenticated(state)) {
        return RoutingTable.resolve(RoutingTable.ROUTING_KINDS.login, { context: selectors.getLoginContext?.(state) || {} });
    }

    return RoutingTable.getIncompleteSignupRoute({ signupContext: selectors.getSignupContext?.(state) || {}, isAuthenticated: true });
}

function navigateToPath({ path, store, record = Diagnostics.record }) {

    if (!path) {
        return;
    }

    record('navigation.dispatch', { destination: Diagnostics.safeLocation(path) });
    if (store?.dispatch) {
        store.dispatch(replace(path));
        return;
    }

    if (window.history?.replaceState) {
        window.history.replaceState({}, document.title, path);
        window.dispatchEvent(new PopStateEvent('popstate'));
        return;
    }

    window.location.replace(path);
}
